import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

/**
 * Orders backed by the Purchase collection.
 *
 * Distinct from the Transactions tab, which lists Stripe payment intents.
 * That answers "what did we charge"; this answers "what do we owe someone" —
 * fulfilment state, shipping, and the ability to move an order along.
 *
 * Stripe detail is fetched per order on demand rather than for the whole list,
 * because it costs an API call each and is only useful once you are looking at
 * a specific order.
 */

interface OrderItem {
  productName?: string;
  quantity?: number;
  unitAmount?: number;
  currency?: string;
}

interface Order {
  _id: string;
  stripeSessionId: string;
  stripePaymentIntentId?: string;
  productName: string;
  productSize?: string;
  quantity: number;
  items?: OrderItem[];
  customerEmail: string;
  customerName?: string;
  shippingAddress?: {
    name?: string; line1?: string; line2?: string;
    city?: string; state?: string; postalCode?: string; country?: string;
  };
  paymentStatus: string;
  orderStatus: string;
  amountTotal: number;
  amountSubtotal?: number;
  taxAmount?: number;
  shippingAmount?: number;
  currency: string;
  trackingNumber?: string;
  shippingDate?: string;
  purchasedAt: string;
  createdAt: string;
  confirmationNumber?: string;
  confirmationEmailSentAt?: string;
  confirmationEmailError?: string;
}

interface StripeDetail {
  chargeId?: string;
  payment?: {
    paid: boolean;
    receiptUrl?: string;
    cardBrand?: string;
    cardLast4?: string;
    riskLevel?: string;
    disputed: boolean;
  } | null;
  settlement?: { gross: number; fee: number; net: number; available: string } | null;
  refunds?: { id: string; amount: number; reason?: string; status: string }[];
  amountRefunded?: number;
}

const ORDER_STATUSES = ['pending', 'confirmed', 'shipped', 'delivered', 'canceled'];

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  canceled: 'bg-red-100 text-red-800',
  paid: 'bg-green-100 text-green-800',
  unpaid: 'bg-red-100 text-red-800',
  no_payment_required: 'bg-gray-100 text-gray-700',
};

const money = (minorUnits: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((minorUnits || 0) / 100);

const OrdersComponent: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [stripeDetail, setStripeDetail] = useState<Record<string, StripeDetail>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const notify = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiService.getPurchases({
        limit: 50,
        ...(statusFilter ? { orderStatus: statusFilter } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      }) as unknown as { data: { purchases: Order[]; pagination: { total: number } } };

      setOrders(res.data?.purchases || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load orders');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Pulled per order, only when opened.
  const toggleExpand = async (order: Order) => {
    if (expandedId === order._id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(order._id);

    if (stripeDetail[order._id]) return;

    try {
      const res = await apiService.getOrderStripeDetail(order._id) as unknown as { stripe: StripeDetail };
      setStripeDetail((prev) => ({ ...prev, [order._id]: res.stripe }));
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not read Stripe detail');
    }
  };

  const changeStatus = async (order: Order, orderStatus: string) => {
    if (busyId) return;
    setBusyId(order._id);
    try {
      await apiService.updateOrderStatus(order._id, { orderStatus });
      notify('success', `Order marked ${orderStatus}`);
      await loadOrders();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not update status');
    } finally {
      setBusyId(null);
    }
  };

  const resendConfirmation = async (order: Order) => {
    if (busyId) return;
    setBusyId(order._id);
    try {
      const res = await apiService.resendOrderConfirmation(order._id) as unknown as {
        sent: boolean; message: string;
      };
      // The endpoint reports a refusal as a successful request with sent:false,
      // so the provider's reason reaches the operator instead of a generic error.
      notify(res.sent ? 'success' : 'error', res.message);
      await loadOrders();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not resend');
    } finally {
      setBusyId(null);
    }
  };

  const syncOrder = async (order: Order) => {
    if (busyId) return;
    setBusyId(order._id);
    try {
      const res = await apiService.syncOrderWithStripe(order._id) as unknown as {
        inSync: boolean; changes: { field: string }[]; message: string;
      };
      notify('success', res.message);
      if (!res.inSync) await loadOrders();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`rounded-xl px-6 py-4 ${
          message.type === 'success'
            ? 'bg-scarf-green/10 border border-scarf-green/30 text-scarf-green'
            : 'bg-brand-red/10 border border-brand-red/30 text-brand-red'
        }`}>
          <p className="font-body">{message.text}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name, or Stripe id"
            className="flex-1 px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy placeholder-gray-400 focus:border-brand-blue outline-none font-body"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy font-body"
          >
            <option value="">All statuses</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <button
            onClick={loadOrders}
            className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-brand-red/10 border border-brand-red/30 rounded-xl px-6 py-4">
          <p className="font-body text-brand-red">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">
          {total} order{total === 1 ? '' : 's'}
        </h3>

        {isLoading ? (
          <p className="text-gray-600 font-body py-8 text-center">Loading…</p>
        ) : orders.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-gray-600 font-body">No orders yet.</p>
            <p className="text-gray-500 font-body text-sm mt-2">
              Orders appear here once a checkout completes and Stripe confirms payment.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const detail = stripeDetail[order._id];
              const isOpen = expandedId === order._id;

              return (
                <div key={order._id} className="border border-ice-teal-light rounded-xl overflow-hidden">
                  {/* Summary row */}
                  <button
                    onClick={() => toggleExpand(order)}
                    className="w-full text-left p-4 hover:bg-frost-white transition-colors"
                  >
                    <div className="flex flex-wrap items-center gap-3 justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-deep-navy font-body truncate">
                          {order.customerName || order.customerEmail}
                        </p>
                        <p className="text-sm text-gray-600 font-body truncate">
                          {order.productName}
                          {order.productSize && ` · ${order.productSize}`}
                          {order.quantity > 1 && ` · ×${order.quantity}`}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="font-bold text-deep-navy font-body">
                          {money(order.amountTotal, order.currency)}
                        </span>
                        <span className={`text-xs font-bold px-2 py-1 rounded ${STATUS_STYLES[order.paymentStatus] || 'bg-gray-100 text-gray-700'}`}>
                          {order.paymentStatus}
                        </span>
                        <span className={`text-xs font-bold px-2 py-1 rounded ${STATUS_STYLES[order.orderStatus] || 'bg-gray-100 text-gray-700'}`}>
                          {order.orderStatus}
                        </span>
                        <span className={`text-gray-400 transition-transform inline-block ${isOpen ? 'rotate-90' : ''}`}>›</span>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-ice-teal-light bg-frost-white p-4 space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Order */}
                        <div>
                          <p className="text-xs font-bold text-gray-600 font-body mb-2">ORDER</p>
                          <dl className="text-sm font-body space-y-1">
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Placed</dt>
                              <dd className="text-deep-navy">{new Date(order.purchasedAt || order.createdAt).toLocaleString()}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Subtotal</dt>
                              <dd className="text-deep-navy">{money(order.amountSubtotal || 0, order.currency)}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Shipping</dt>
                              <dd className="text-deep-navy">{money(order.shippingAmount || 0, order.currency)}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Tax</dt>
                              <dd className="text-deep-navy">{money(order.taxAmount || 0, order.currency)}</dd>
                            </div>
                            <div className="flex justify-between font-bold">
                              <dt className="text-deep-navy">Total</dt>
                              <dd className="text-deep-navy">{money(order.amountTotal, order.currency)}</dd>
                            </div>
                          </dl>

                          {(order.items?.length || 0) > 1 && (
                            <div className="mt-3">
                              <p className="text-xs font-bold text-gray-600 font-body mb-1">ITEMS</p>
                              {order.items?.map((item, i) => (
                                <p key={i} className="text-sm text-deep-navy font-body">
                                  {item.quantity}× {item.productName}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Customer */}
                        <div>
                          <p className="text-xs font-bold text-gray-600 font-body mb-2">CUSTOMER</p>
                          <p className="text-sm text-deep-navy font-body">{order.customerEmail}</p>

                          {order.confirmationEmailSentAt ? (
                            <p className="text-xs text-scarf-green font-body mt-1">
                              Confirmation sent {new Date(order.confirmationEmailSentAt).toLocaleString()}
                            </p>
                          ) : order.confirmationEmailError ? (
                            <p className="text-xs text-brand-red font-body mt-1">
                              Confirmation failed: {order.confirmationEmailError}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 font-body mt-1">
                              No confirmation sent yet
                            </p>
                          )}
                          {order.shippingAddress?.line1 && (
                            <address className="text-sm text-deep-navy font-body not-italic mt-2">
                              {order.shippingAddress.name}<br />
                              {order.shippingAddress.line1}<br />
                              {order.shippingAddress.line2 && <>{order.shippingAddress.line2}<br /></>}
                              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}<br />
                              {order.shippingAddress.country}
                            </address>
                          )}
                          {order.trackingNumber && (
                            <p className="text-sm text-brand-blue font-body mt-2">
                              Tracking: {order.trackingNumber}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Live Stripe detail */}
                      <div className="border-t border-ice-teal-light pt-4">
                        <p className="text-xs font-bold text-gray-600 font-body mb-2">STRIPE</p>
                        {!detail ? (
                          <p className="text-sm text-gray-500 font-body">Loading from Stripe…</p>
                        ) : (
                          <div className="grid md:grid-cols-3 gap-4 text-sm font-body">
                            <div>
                              <p className="text-gray-600">Payment</p>
                              {detail.payment ? (
                                <>
                                  <p className="text-deep-navy">
                                    {detail.payment.cardBrand} ····{detail.payment.cardLast4}
                                  </p>
                                  {detail.payment.receiptUrl && (
                                    <a
                                      href={detail.payment.receiptUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-brand-blue hover:text-brand-blue-dark font-bold"
                                    >
                                      View receipt →
                                    </a>
                                  )}
                                  {detail.payment.disputed && (
                                    <p className="text-brand-red font-bold">Disputed</p>
                                  )}
                                </>
                              ) : (
                                <p className="text-gray-500">No charge found</p>
                              )}
                            </div>

                            <div>
                              <p className="text-gray-600">Settlement</p>
                              {detail.settlement ? (
                                <>
                                  <p className="text-deep-navy">
                                    Fee {money(detail.settlement.fee, order.currency)}
                                  </p>
                                  <p className="text-scarf-green font-bold">
                                    Net {money(detail.settlement.net, order.currency)}
                                  </p>
                                </>
                              ) : (
                                <p className="text-gray-500">Not yet settled</p>
                              )}
                            </div>

                            <div>
                              <p className="text-gray-600">Refunds</p>
                              {detail.amountRefunded ? (
                                <p className="text-brand-red font-bold">
                                  {money(detail.amountRefunded, order.currency)} refunded
                                </p>
                              ) : (
                                <p className="text-deep-navy">None</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="border-t border-ice-teal-light pt-4 flex flex-wrap gap-2 items-center">
                        <span className="text-xs font-bold text-gray-600 font-body mr-1">MARK AS</span>
                        {ORDER_STATUSES.filter((st) => st !== order.orderStatus).map((st) => (
                          <button
                            key={st}
                            onClick={() => changeStatus(order, st)}
                            disabled={busyId !== null}
                            className="bg-white hover:bg-ice-teal/20 text-deep-navy font-bold px-3 py-1.5 rounded-lg text-sm border border-ice-teal-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {st}
                          </button>
                        ))}

                        <button
                          onClick={() => resendConfirmation(order)}
                          disabled={busyId !== null || !order.customerEmail}
                          className="bg-white hover:bg-ice-teal/20 text-deep-navy font-bold px-3 py-1.5 rounded-lg text-sm border border-ice-teal-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title={order.customerEmail
                            ? `Resend the confirmation to ${order.customerEmail}`
                            : 'This order has no email address'}
                        >
                          {busyId === order._id ? 'Sending…' : 'Resend confirmation'}
                        </button>

                        <button
                          onClick={() => syncOrder(order)}
                          disabled={busyId !== null}
                          className="ml-auto bg-frost-white hover:bg-ice-teal/20 text-deep-navy font-bold px-3 py-1.5 rounded-lg text-sm border border-ice-teal-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Re-read this order from Stripe and reconcile any differences"
                        >
                          {busyId === order._id ? 'Working…' : 'Sync with Stripe'}
                        </button>
                      </div>

                      <p className="text-xs text-gray-400 font-mono break-all">
                        {order.stripeSessionId}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersComponent;
